import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import { CalendarDays, Briefcase, CalendarOff, Clock, Building2 } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { OfficeCalendarTab } from "./OfficeCalendarTab";
import { OfficeJobsTab } from "./OfficeJobsTab";
import { OfficeTimeOffTab } from "./OfficeTimeOffTab";
import { OfficeWorkingHoursTab } from "./OfficeWorkingHoursTab";
import { OfficeBusinessHoursTab } from "./OfficeBusinessHoursTab";

const TAB_DEFS = [
  { id: "calendar", label: "Calendar", icon: CalendarDays, levels: ["org", "team", "self"] },
  { id: "jobs", label: "Jobs", icon: Briefcase, levels: ["org", "team", "self"] },
  { id: "time_off", label: "Time Off", icon: CalendarOff, levels: ["org", "team", "self"] },
  { id: "working_hours", label: "Working Hours", icon: Clock, levels: ["org", "team"] },
  { id: "business_hours", label: "Business Hours", icon: Building2, levels: ["org"] },
];

export const OfficeWorkspace = () => {
  const { API } = useAuth();
  const [searchParams] = useSearchParams();
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState("");
  const [tab, setTab] = useState(searchParams.get("tab") || "calendar");

  const loadMeta = useCallback(() => {
    axios.get(`${API}/org/office/meta`, { withCredentials: true })
      .then(({ data }) => setMeta(data))
      .catch((err) => setError(formatApiErrorDetail(err.response?.data?.detail)));
  }, [API]);

  useEffect(() => { loadMeta(); }, [loadMeta]);

  if (error) return <p className="rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 dark:bg-red-500/10 dark:text-red-400" data-testid="office-error">{error}</p>;
  if (!meta) return <p className="text-sm text-slate-500 dark:text-slate-400" data-testid="office-loading">Loading…</p>;

  const visibleTabs = TAB_DEFS.filter((t) => t.levels.includes(meta.scope.level));
  const activeTab = visibleTabs.some((t) => t.id === tab) ? tab : "calendar";
  const deepLinkScope = { scopeKind: searchParams.get("scope_kind"), teamId: searchParams.get("team_id"), userId: searchParams.get("target_user_id") };

  return (
    <div data-testid="office-workspace">
      <div className="flex flex-wrap items-center gap-1 border-b border-slate-200 dark:border-white/10" data-testid="office-tabs">
        {visibleTabs.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            data-testid={`office-tab-${t.id}`}
            className={`flex items-center gap-1.5 border-b-2 px-3.5 py-2.5 text-sm font-bold transition-colors duration-150 ${activeTab === t.id ? "border-[#0052FF] text-[#0052FF]" : "border-transparent text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white"}`}
          >
            <t.icon size={15} /> {t.label}
          </button>
        ))}
      </div>

      <div className="mt-5">
        {activeTab === "calendar" && <OfficeCalendarTab meta={meta} API={API} deepLinkScope={deepLinkScope} />}
        {activeTab === "jobs" && <OfficeJobsTab meta={meta} API={API} />}
        {activeTab === "time_off" && <OfficeTimeOffTab meta={meta} API={API} />}
        {activeTab === "working_hours" && <OfficeWorkingHoursTab meta={meta} API={API} />}
        {activeTab === "business_hours" && <OfficeBusinessHoursTab meta={meta} API={API} onSaved={loadMeta} />}
      </div>
    </div>
  );
};

